import "./receptionTypes.js";
import React, { memo } from "react";
import { alpha, Box, Button, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  appointmentAdhocType,
  NO_SHOW_M,
  status,
} from "shared/src/appointment.mjs";
import * as f from "../../utils/formatter";
import { appointmentDelayedBy } from "./utils";
import { ClientName } from "./ClientName";
import { ConfirmationModal } from "./ConfirmationModal";
import { createClickHandler } from "../calendar";
import { createUrlString } from "../../url";
import { faLinkHorizontal } from "@fortawesome/pro-solid-svg-icons";
import { canBeTreated, hasBlocker } from "shared/src/client.mjs";

export const BookedClient = memo(
  /**
   * A component to display a booked client on the reception dashboard
   *
   * @typedef {object} BookedClientProps
   * @property {Appointment} appointment
   * @property {Appointment | null} childAppointment
   * @property {function} setSnackbarMessage Setter function for the snackbar message
   * @property {function} setSnackbarTitle Setter function for the snackbar title
   * @property {function} setSnackbarOpen Setter function for the snackbar open state
   * @property {function} setNewAdhocDialogOptions Setter function for the Adhoc dialog options
   * @property {string} hoverOverId
   * @property {function} setHoverOverId Setter function for the hoverOverId state
   * @property {Set<string>} nameDayNames
   *
   * @param {BookedClientProps} props
   * @returns {ReactElement}
   */
  ({
    appointment,
    setNewAdhocDialogOptions,
    hoverOverId,
    setHoverOverId,
    nameDayNames,
    setShowDetailModal,
    setMaybeShowDetailModal,
  }) => {
    const delayedBy = appointmentDelayedBy(appointment.start);
    const [openConfirmationModal, setOpenConfirmationModal] =
      React.useState(false);

    const handleMouseEnter = (id) => {
      if (id) {
        setHoverOverId(id);
      }
    };

    const handleMouseLeave = () => {
      setHoverOverId("");
    };

    const handleOpenConfirmationModal = () => {
      setOpenConfirmationModal(true);
    };

    const handleCloseConfirmationModal = () => {
      setOpenConfirmationModal(false);
    };

    const clickHandler = createClickHandler({
      onClick: () => {},
      onDoubleClick: () => {
        const clientId = appointment.client?.id;
        if (clientId == null) {
          return;
        }

        window.open(
          createUrlString(
            location.origin,
            location.search,
            `/clients/${clientId}`,
          ),
          "_blank",
        );
      },
    });

    const theme = useTheme();

    return (
      <>
        <Box
          sx={{
            position: "relative",
            borderBottomStyle: "solid",
            borderBottomColor: "divider",
            borderWidth: 1,
            padding: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            backgroundColor:
              appointment.group_id === hoverOverId ||
              appointment.reservation_id === hoverOverId
                ? alpha(theme.palette.primary.main, 0.2)
                : "background.paper",
          }}
          onClick={clickHandler}
          onMouseEnter={() =>
            handleMouseEnter(appointment.group_id ?? appointment.reservation_id)
          }
          onMouseLeave={handleMouseLeave}
          data-test-id="booked-client"
          data-test-email={appointment?.client?.email}
        >
          <Box sx={{ flex: "0 0 70%", minWidth: "0", paddingRight: "10px" }}>
            <ClientName appointment={appointment} nameDayNames={nameDayNames} />
          </Box>
          <Box sx={{ flex: "0 0 20%", fontWeight: "bold", color: "grey" }}>
            {f.time24(appointment.start)}{" "}
            {!!delayedBy && (
              <Box
                component="span"
                sx={{ color: "_components.alert.error.color" }}
              >
                ({f.minutesToDuration(delayedBy)})
              </Box>
            )}
          </Box>
          <Box sx={{ flex: "0 0 10%", textAlign: "right" }}>
            {appointmentDelayedBy(appointment.start) <= NO_SHOW_M && (
              <Button
                disabled={!canBeTreated(appointment.client)}
                variant="contained"
                color="secondary"
                sx={{
                  display: "inline-flex",
                  borderRadius: 1,
                  color: "white",
                  width: "2.6rem",
                  height: "2.6rem",
                  fontSize: "1.8rem",
                  minWidth: 0,
                  px: 0,
                }}
                onClick={handleOpenConfirmationModal}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
            {appointmentDelayedBy(appointment.start) > NO_SHOW_M && (
              <Button
                disabled={!canBeTreated(appointment.client)}
                variant="contained"
                color="error"
                sx={{
                  display: "inline-flex",
                  borderRadius: 1,
                  color: "white",
                  width: "2.6rem",
                  height: "2.6rem",
                  fontSize: "1.8rem",
                  minWidth: 0,
                  px: 0,
                }}
                onClick={() => {
                  return setNewAdhocDialogOptions({
                    close: () => setNewAdhocDialogOptions(null),
                    defaults: {
                      appointment,
                      client: appointment.client,
                      serviceOption: {
                        ...appointment.service,
                        duration: appointment.service.locationService.duration,
                      },
                      types: [appointmentAdhocType.NOSHOW],
                    },
                    onConfirm: (appointmentId) => {
                      setMaybeShowDetailModal(appointmentId);
                    },
                  });
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
          </Box>
          {appointment.group_id === hoverOverId && (
            <Box
              component="span"
              sx={{
                position: "absolute",
                left: "50%",
                bottom: "5px",
                color: "primary.dark",
              }}
            >
              <FontAwesomeIcon icon={faLinkHorizontal} />
            </Box>
          )}
        </Box>
        {openConfirmationModal && (
          <ConfirmationModal
            appointment={appointment}
            handleCloseConfirmationModal={handleCloseConfirmationModal}
            newStatus={
              hasBlocker(appointment.client) ? status.CONFIRMED : status.WAITING
            }
            onConfirm={() => {
              if (hasBlocker(appointment.client)) {
                setShowDetailModal(appointment);
              }
            }}
          />
        )}
      </>
    );
  },
);

BookedClient.displayName = "BookedClient";
